<template>
	<Datepicker
		week-numbers
		week-num-name="U"
		v-model="date"
		range
		multi-calendars
		locale="da"
		input-class-name="form-control"
		:format="dateRange"
		:day-names="['Ma', 'Ti', 'On', 'To', 'Fr', 'Lø', 'Sø']"
		:auto-apply="true"
		:close-on-auto-apply="true"
		:enable-time-picker="false"
		:preset-dates="presetRanges"
		@update:modelValue="dateChanged"
	>
		<template #lastWeek="{ label, range, presetDateRange }">
			<span @click="presetDateRange(range)">{{ label }}</span>
		</template>
		<template #input-icon>
			<span class="input-group-text">
				<font-awesome-icon icon="calendar-check" height="1rem" class="d-flex"/>
			</span>
		</template>
		<template #clear-icon />
	</Datepicker>
</template>

<script setup>
import { ref, onMounted } from "vue";

import Datepicker from "@vuepic/vue-datepicker";
import { format, isEqual, addDays, addWeeks, addMonths, addYears } from "date-fns";
import "@vuepic/vue-datepicker/dist/main.css";
import "@oldSrc/assets/datepicker.css";

const props = defineProps({
	isStartDateWeekBefore: {
		type: Boolean,
		default: false
	},
	isBvc: {
		type: Boolean,
		default: false
	},
	yearsToAdd: {
		type: Number,
		default: null,
	},
	startDate: {
		type: Date,
		default: null,
	},
	endDate: {
		type: Date,
		default: null,
	},
	minDate: {
		type: Date,
		default: null,
	},
	maxDate: {
		type: Date,
		default: null,
	},
});

const minDate = new Date(props.minDate ? props.minDate : Date.UTC(2018, 0, 1));
const maxDate = new Date(props.maxDate ? props.maxDate : addDays(new Date(), 1));

const startDate = props.isStartDateWeekBefore || props.isBvc ? new Date(addWeeks(new Date(), -1)) : props.startDate ? props.startDate : new Date();
const endDate = props.isStartDateWeekBefore || props.isBvc ? new Date() : props.endDate ? props.endDate : new Date(addWeeks(new Date(), 1));
const date = ref([startDate, endDate]);

date.value = [startDate, endDate];

const dateRange = () => {
	return isEqual(date.value[0], minDate) && isEqual(date.value[1], maxDate) ? 'Alle' : `${format(date.value[0], "dd/MM/yyyy")} - ${format(
		date.value[1],
		"dd/MM/yyyy"
	)}`;
}

const emit = defineEmits(["change"]);

const dateChanged = () => {
	if (dateRange() === "Alle" && props.yearsToAdd) {
		const endDate = new Date(addYears(new Date(), props.yearsToAdd));
		emit("change", ref([startDate, endDate]));
	} else {
		emit("change", date);
	}
};

onMounted(() => {
	emit("change", date);
});

const todaysDate = new Date();

const presetRanges = props.isStartDateWeekBefore ? ref([
	{
		label: "Seneste døgn",
		value: [addDays(todaysDate, -1), todaysDate],
	},
	{
		label: "Seneste to døgn",
		value: [addDays(todaysDate, -2), todaysDate],
	},
	{
		label: "Seneste uge",
		value: [addWeeks(todaysDate, -1), todaysDate],
	},
	{
		label: "Seneste måned",
		value: [addMonths(todaysDate, -1), todaysDate],
	},
	{
		label: "Seneste 6 måneder",
		value: [addMonths(todaysDate, -6), todaysDate],
	},
	{
		label: "Alle",
		value: [new Date(minDate), new Date(maxDate)],
	},
]) : props.isBvc ? ref([
{
		label: "Seneste døgn",
		value: [addDays(todaysDate, -1), todaysDate],
	},
	{
		label: "Seneste to døgn",
		value: [addDays(todaysDate, -2), todaysDate],
	},
	{
		label: "Seneste uge",
		value: [addWeeks(todaysDate, -1), todaysDate],
	},
	{
		label: "Seneste 2 uger",
		value: [addWeeks(todaysDate, -2), todaysDate],
	},
	{
		label: "Seneste 3 uger",
		value: [addWeeks(todaysDate, -3), todaysDate],
	},
	{
		label: "Seneste måned",
		value: [addMonths(todaysDate, -1), todaysDate],
	},
]) : ref([
	{
		label: "Næste døgn",
		value: [startDate, addDays(startDate, 1)],
	},
	{
		label: "Næste to døgn",
		value: [startDate, addDays(startDate, 2)],
	},
	{
		label: "Næste uge",
		value: [startDate, addWeeks(startDate, 1)],
	},
	{
		label: "Næste måned",
		value: [startDate, addMonths(startDate, 1)],
	},
	{
		label: "Næste 6 måneder",
		value: [startDate, addMonths(startDate, 6)],
	},
	{
		label: "Alle",
		value: [new Date(minDate), new Date(maxDate)],
	},
]);
</script>
